// Centralized debug logging utility for the entire project

// Debug flags - set to true to enable logging for specific areas
// Flag names match log prefixes for clarity (e.g., ANNI_CHAT controls "[ANNI CHAT]" logs)
export const DEBUG_FLAGS = {
  SELECTORS: false,
  AI: false,
  STORAGE: false,
  AUTH: false,
  SYNC: false,
  GENERAL: false,
  APPLICATION: false, // For general application logging
  RENDERING: false, // For annotation rendering and initialization
  UI: false, // For UI component logging
  CONTEXT: false, // For React context logging
  CONTENT_SCRIPT: false, // For extension content script logging
  ANNI_CHAT: false, // For "[ANNI CHAT]" logs
  AUTO_SUGGESTIONS: false, // For "[AUTO SUGGESTIONS]" logs
};

// Runtime debug configuration
interface DebugConfig {
  isStaffUser: boolean;
  isDevelopment: boolean;
  enabledAreas: Set<keyof typeof DEBUG_FLAGS>;
}

// Global debug configuration - can be updated at runtime
let debugConfig: DebugConfig = {
  isStaffUser: false,
  isDevelopment: process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test',
  enabledAreas: new Set(),
};

// Initialize debug areas based on static flags and environment
Object.entries(DEBUG_FLAGS).forEach(([area, enabled]) => {
  if (enabled) {
    debugConfig.enabledAreas.add(area as keyof typeof DEBUG_FLAGS);
  }
});

// Update debug configuration at runtime (call this when user auth state changes)
export const updateDebugConfig = (config: Partial<DebugConfig>): void => {
  debugConfig = { ...debugConfig, ...config };
};

// Enable debugging for staff users
export const enableStaffDebugging = (areas: (keyof typeof DEBUG_FLAGS)[] = ['APPLICATION']): void => {
  areas.forEach((area) => debugConfig.enabledAreas.add(area));
};

// Disable all debugging (useful for non-staff users)
export const disableAllDebugging = (): void => {
  debugConfig.enabledAreas.clear();
};

// Check if logging is enabled for a specific area
const isLoggingEnabled = (area: keyof typeof DEBUG_FLAGS): boolean => {
  // If the area is explicitly disabled in DEBUG_FLAGS, never enable it (even in development)
  // However, if it was explicitly enabled via enabledAreas (runtime), allow it
  if (DEBUG_FLAGS[area] === false) {
    // Only disable if not explicitly enabled at runtime
    if (!debugConfig.enabledAreas.has(area)) {
      return false;
    }
  }

  // In development, enable by default (unless explicitly disabled above)
  if (debugConfig.isDevelopment) {
    return true;
  }

  // In production, only log if user is staff and area is enabled
  return debugConfig.isStaffUser && debugConfig.enabledAreas.has(area);
};

// Log levels
export enum LogLevel {
  DEBUG = 'DEBUG',
  INFO = 'INFO',
  WARN = 'WARN',
  ERROR = 'ERROR',
}

// Logger interface
interface Logger {
  debug: (...args: unknown[]) => void;
  info: (...args: unknown[]) => void;
  warn: (...args: unknown[]) => void;
  error: (...args: unknown[]) => void;
  // Staff-only logging (only shows for staff users, regardless of debug flags)
  staff: (...args: unknown[]) => void;
}

// Helper to format area name for display (converts ANNI_CHAT -> "ANNI CHAT")
const formatAreaName = (area: string): string => {
  return area.replace(/_/g, ' ');
};

// Create a logger for a specific area/component
export const createLogger = (area: keyof typeof DEBUG_FLAGS): Logger => {
  const displayName = formatAreaName(area);
  return {
    debug: (...args: unknown[]) => {
      if (isLoggingEnabled(area)) {
        console.log(`[${displayName}]`, ...args);
      }
    },
    info: (...args: unknown[]) => {
      if (isLoggingEnabled(area)) {
        console.info(`[${displayName}]`, ...args);
      }
    },
    warn: (...args: unknown[]) => {
      if (isLoggingEnabled(area)) {
        console.warn(`[${displayName}]`, ...args);
      }
    },
    error: (...args: unknown[]) => {
      // Always show errors, but prefix with area for staff users
      // AUTH errors are always shown in full (even for non-staff) to help debug login issues
      if (debugConfig.isStaffUser || debugConfig.isDevelopment || area === 'AUTH') {
        console.error(`[${displayName}]`, ...args);
      } else {
        // For regular users, show generic error without internal details
        console.error('Annote: An error occurred. Please contact support if this persists.');
      }
    },
    staff: (...args: unknown[]) => {
      // Only log for staff users or in development
      if (debugConfig.isStaffUser || debugConfig.isDevelopment) {
        console.log(`[${displayName}]`, ...args);
      }
    },
  };
};

// Convenience loggers for common areas
export const selectorLogger = createLogger('SELECTORS');
export const aiLogger = createLogger('AI');
export const storageLogger = createLogger('STORAGE');
export const authLogger = createLogger('AUTH');
export const syncLogger = createLogger('SYNC');
export const generalLogger = createLogger('GENERAL');
export const appLogger = createLogger('APPLICATION'); // For general app logging
export const renderingLogger = createLogger('RENDERING'); // For annotation rendering and initialization
export const uiLogger = createLogger('UI'); // For UI component logging
export const contextLogger = createLogger('CONTEXT'); // For React context logging
export const contentScriptLogger = createLogger('CONTENT_SCRIPT'); // For extension content script logging
export const anniChatLogger = createLogger('ANNI_CHAT'); // For "[ANNI CHAT]" logs
export const autoSuggestionsLogger = createLogger('AUTO_SUGGESTIONS'); // For "[AUTO SUGGESTIONS]" logs
